import {
  compact as compactValue,
  formatPrice,
  formatTimeFromMinutes,
} from '@leland-dev/leland-ui-library';

import {
  type IntRangeInput,
  type PackageCoachingTime,
  RefundPolicy,
  type Scalars,
  type SkuTier,
} from '../__generated-gql-types__/globalTypes';
import { type EstimatedSkuPrice, type PlanPrice } from '../types/plan-price';

export enum CoachingTimeType {
  EXACT = 'EXACT',
  ESTIMATE = 'ESTIMATE',
}

import { type PackagesUtilOfferingPackagePriceFragment } from './__generated-gql-types__/packages.generated';
import { COMPREHENSIVE_PACKAGE_NAME } from './constants';
import { getUrlString } from './url';

export interface OfferingPackagesFilterInputs {
  keyword?: string | null;
  priceFilter?: IntRangeInput | null;
  coachingFocusListFilter?: Array<Scalars['CoachingFocusUrn']['output']> | null;
  schoolsSort?: Array<Scalars['SchoolUrn']['output']> | null;
}

export interface OfferingPackagesFiltersDefaultValues {
  selectedFilters?: OfferingPackagesFilterInputs | null;
  totalOfferingPackages: number;
}

export const PACKAGE_IMAGE_ASPECT_RATIOS = {
  WIDTH_TO_HEIGHT: 9 / 5,
  HEIGHT_TO_WIDTH: 5 / 9,
};

export const getPackageListingPageUrl = ({
  coachSlug,
  packageSlug,
  packageId,
}: {
  coachSlug: string;
  packageSlug: Possible<string>;
  packageId: Scalars['PackageUrn']['output'];
}): string => `/coach/${coachSlug}/p/${packageSlug ?? packageId}`;

export const getPlanCustomerPageUrl = ({
  categorySlug,
  coachTier,
  packageSlug,
  coachId,
}: {
  categorySlug: Possible<Scalars['CategoryUrn']['output']>;
  coachId?: Scalars['CoachUrn']['output'];
  coachTier: Possible<SkuTier>;
  packageSlug: Possible<Scalars['PackageUrn']['output']>;
}): string =>
  getUrlString(`/package/${categorySlug}/${packageSlug}`, {
    opportunityCoachId: coachId,
    coachTier,
  });

type Prices = Possible<
  Array<{
    price: number;
    packageCoachingTime?: Nullable<PackageCoachingTime>;
  }>
>;

export const getCheapestOfferingPackagePrice = (
  prices: Prices,
): number | null => {
  const cheapestPrice = prices?.map((p) => p.price).sort((a, b) => a - b)[0];

  return cheapestPrice ?? null;
};

export const getOfferingPackagePriceLabel = (prices: Prices): string =>
  `${prices?.length !== 1 ? 'Starting at ' : ''}${formatPrice(
    getCheapestOfferingPackagePrice(prices) ?? 0,
  )}`;

export const validateExactTime = (
  exactTimeInMinute: Possible<number>,
): boolean => !!exactTimeInMinute;

export const getOfferingPackageCoachingTimeLabel = (
  prices: Possible<
    Array<{
      packageCoachingTime?: Nullable<PackageCoachingTime>;
    }>
  >,
  {
    compact,
    longFormatting,
  }: { compact?: boolean; longFormatting?: boolean } = {},
): Nullable<string> => {
  let hoursLabel: Nullable<string> = null;

  if (prices?.length === 1) {
    if (
      prices[0].packageCoachingTime?.exactTimeInMinute &&
      validateExactTime(prices[0].packageCoachingTime.exactTimeInMinute)
    ) {
      hoursLabel = formatTimeFromMinutes(
        prices[0].packageCoachingTime.exactTimeInMinute,
        { longFormatting },
      );
    }

    if (
      prices[0].packageCoachingTime?.estimate?.start &&
      prices[0].packageCoachingTime.estimate.end
    ) {
      // \u2013 — en dash
      hoursLabel = `${prices[0].packageCoachingTime.estimate.start}h\u2013${prices[0].packageCoachingTime.estimate.end}h`;
    }
  } else if ((prices?.length ?? 0) > 1) {
    const allTimesInMinutes =
      prices
        ?.map((tier) => {
          if (
            tier.packageCoachingTime?.exactTimeInMinute &&
            validateExactTime(tier.packageCoachingTime.exactTimeInMinute)
          ) {
            return tier.packageCoachingTime.exactTimeInMinute;
          } else if (tier.packageCoachingTime?.estimate) {
            return tier.packageCoachingTime.estimate.start
              ? tier.packageCoachingTime.estimate.start * 60
              : Infinity;
          }
        })
        .filter(compactValue) ?? [];
    const maxTimeInMinutes = Math.max(...allTimesInMinutes);
    const minTimeInMinutes = Math.min(...allTimesInMinutes);
    if (minTimeInMinutes !== Infinity) {
      hoursLabel = `${formatTimeFromMinutes(minTimeInMinutes)}${
        minTimeInMinutes === maxTimeInMinutes ? '' : '+'
      }`;
    }
  }

  return hoursLabel
    ? compact
      ? hoursLabel
      : `${hoursLabel} of coaching`
    : null;
};

export const REFUND_POLICY_DETAILS: Record<RefundPolicy, string> = {
  [RefundPolicy.FLEXIBLE]:
    'You can request a refund for any unused coaching before expiration term.',
  [RefundPolicy.MODERATE]:
    'Refunds are available within 14 days of purchase. Leland credit can be issued if the order has not expired yet.',
  [RefundPolicy.STRICT]: 'No refunds.',
};

export const REFUND_POLICY_HUMAN_READABLE: Record<RefundPolicy, string> = {
  [RefundPolicy.FLEXIBLE]: 'Flexible Refund Policy',
  [RefundPolicy.MODERATE]: 'Refund Policy',
  [RefundPolicy.STRICT]: 'Strict Refund Policy',
};

export const offeringPackagePricesToPlanPrices = (
  opPrices: PackagesUtilOfferingPackagePriceFragment[],
): PlanPrice[] => {
  const planPrices: Record<string, PlanPrice> = {};

  opPrices.forEach((opPrice) => {
    if (!opPrice.sku || !opPrice.tier) return;
    const exactTierTime = opPrice.packageCoachingTime?.exactTimeInMinute;
    // get the price from the map, if it exists
    const priceInMap =
      planPrices[exactTierTime ? opPrice.sku : COMPREHENSIVE_PACKAGE_NAME];
    if (priceInMap) {
      // push in the correct format if it does exist
      if (priceInMap.tierType === CoachingTimeType.EXACT) {
        priceInMap.prices.push({
          tier: opPrice.tier,
          price: opPrice.price,
        });
      } else {
        priceInMap.tiers.push({
          sku: opPrice.sku,
          minHours: opPrice.packageCoachingTime?.estimate?.start ?? 0,
          maxHours: opPrice.packageCoachingTime?.estimate?.end ?? 0,
          prices: [
            {
              tier: opPrice.tier,
              price: opPrice.price,
            },
          ],
        });
      }
    } else {
      // create and push the price if it doesn't exist
      const planPrice: PlanPrice = exactTierTime
        ? {
            name: opPrice.sku,
            description: opPrice.description,
            tierType: CoachingTimeType.EXACT,
            hours: exactTierTime / 60,
            prices: [
              {
                tier: opPrice.tier,
                price: opPrice.price,
              },
            ],
            freeLelandPlusPeriod: opPrice.freeLelandPlusPeriod ?? undefined,
          }
        : {
            name: COMPREHENSIVE_PACKAGE_NAME,
            description: opPrice.description,
            freeLelandPlusPeriod: opPrice.freeLelandPlusPeriod ?? undefined,

            tierType: CoachingTimeType.ESTIMATE,
            tiers: [
              {
                sku: opPrice.sku,
                minHours: opPrice.packageCoachingTime?.estimate?.start ?? 0,
                maxHours: opPrice.packageCoachingTime?.estimate?.end ?? 0,
                prices: [
                  {
                    tier: opPrice.tier,
                    price: opPrice.price,
                  },
                ],
              },
            ],
          };
      planPrices[exactTierTime ? opPrice.sku : COMPREHENSIVE_PACKAGE_NAME] =
        planPrice;
    }
  });
  // if there is a comprehensive package, convert the tiers to a TierPrice
  if (
    planPrices[COMPREHENSIVE_PACKAGE_NAME] &&
    planPrices[COMPREHENSIVE_PACKAGE_NAME].tierType ===
      CoachingTimeType.ESTIMATE
  ) {
    const compTiers: Record<string, EstimatedSkuPrice[]> = {};
    planPrices[COMPREHENSIVE_PACKAGE_NAME].tiers.forEach((tier) => {
      const skuPrice = compTiers[tier.sku];
      if (skuPrice) {
        skuPrice.push(tier);
      } else {
        compTiers[tier.sku] = [tier];
      }
    });
    planPrices[COMPREHENSIVE_PACKAGE_NAME].tiers = Object.entries(
      compTiers,
    ).map(([sku, skuPrices]) => {
      return {
        sku,
        minHours: skuPrices[0].minHours,
        maxHours: skuPrices[0].maxHours,
        prices: skuPrices.map((p) => p.prices).flat(),
      };
    });
  }
  return Object.values<PlanPrice>(planPrices);
};
